import { useTranslation } from "react-i18next";

import { orgValidationErrorItems, orgValidationErrorKind } from "@/api/diagnostic-org-structure";
import { formatCount } from "@/lib/format";

/**
 * A frase que a tela mostra quando gravar uma validação falha.
 *
 * Sai do status HTTP, e não do `detail` do backend, que vem em português fixo:
 * a frase visível é sempre traduzida, como no resto do Diagnóstico.
 */
export function useValidationErrorMessage() {
  const { t } = useTranslation();

  return (error: unknown): string => {
    switch (orgValidationErrorKind(error)) {
      case "inherited":
        return t("Out of scope because of the level above. To change it, change the level above.");
      case "not_found":
        return t("This unit is no longer in the extraction. Reload the screen.");
      case "invalid":
        return t("This value does not apply to this unit.");
      case "forbidden":
        return t("Your role can view this validation but not change it.");
      default:
        return t("The decision could not be saved. Try again.");
    }
  };
}

/**
 * A frase quando o lote falha (D31). O hook do lote já releu a árvore no erro,
 * então nenhuma frase manda recarregar.
 *
 * - 409 e 404 com a lista dizem quantas unidades (D24) e terminam em "A tela foi relida";
 * - 404 sem unidade nomeada (a lista vazia, que é o 404 de outro tenant) diz que
 *   as selecionadas não puderam ser gravadas;
 * - 422 (a seleção passou do teto do servidor) pede menos unidades;
 * - 403, falha de rede e o resto seguem a frase do PUT de uma unidade.
 */
export function useBatchErrorMessage() {
  const { t } = useTranslation();
  const single = useValidationErrorMessage();

  return (error: unknown): string => {
    const count = orgValidationErrorItems(error);
    const total = formatCount(count ?? 0);
    switch (orgValidationErrorKind(error)) {
      case "inherited":
        if (count) {
          return t(
            "{{total}} of the selected units are out of scope because of the level above. The screen was reloaded.",
            { total },
          );
        }
        break;
      case "not_found":
        if (count) {
          return t(
            "{{total}} of the selected units are no longer in the extraction. The screen was reloaded.",
            { total },
          );
        }
        return t("The selected units could not be saved. The screen was reloaded.");
      case "invalid":
        return t("The selection could not be saved. Select fewer units and try again.");
    }
    return single(error);
  };
}
